import React, { forwardRef, useImperativeHandle, useMemo, useRef } from 'react';

import { useIntl } from 'umi';

import ChartsIndex, {

  type IChartsIndexInstance,

} from '@intelligence/components/ChartsComponents/ChartsIndex';

import Abnormal from '@intelligence/components/Abnormal';

import OpinionStyledLineChart from './OpinionStyledLineChart';

import { shouldUseOpinionLineChart } from './adaptChartData';

import { useOpinionChartEventsWithDateLimit } from './hooks/useOpinionChartEventsWithDateLimit';

import EventSettingBridge from './hooks/EventSettingBridge';

import { OPINION_BAR_AND_LINE_CHART_ROOT_CLASS } from './constants';

import type {

  IOpinionChartEventData,

  IOpinionChartEventSetting,

  IOpinionEventConfig,

} from './hooks/types';



export type { IChartsIndexInstance };



type IOpinionBarAndLineChartProps = Omit<

  React.ComponentProps<typeof ChartsIndex>,

  'onChartEarliestClick'

> & {

  onChartEarliestClick?: (data?: any) => void;

  /** 是否开启舆情事件标注，默认 false */

  enableEventAnnotation?: boolean;

  /** enableEventAnnotation=true 时传入游戏 id、时间范围等 */

  opinionEventConfig?: IOpinionEventConfig;

  /** 页面层受控事件状态，传入时不再在组件内请求事件 */

  controlledEventAnnotation?: {

    eventData: IOpinionChartEventData;

    chartEventSetting: IOpinionChartEventSetting;

    changeEventSetting: (
      setting: IOpinionChartEventSetting,
      options?: { persist?: boolean },
    ) => void;

    eventLoading?: boolean;

    settingStorageKey?: string;

  };

  /** 是否替换轴与 series 结构，避免单/双轴及条件性曲线切换后残留 */
  replaceSeriesOnUpdate?: boolean;

};



export type { IOpinionBarAndLineChartProps };



function getXAxisDataFromLineOption(lineOption?: IRecord | null): string[] {

  const xAxis = lineOption?.xAxis;

  if (Array.isArray(xAxis)) {

    return (xAxis[0]?.data || []).map((item: any) => String(item));

  }

  return (xAxis?.data || []).map((item: any) => String(item));

}



const OpinionBarAndLineChart = forwardRef<

  IChartsIndexInstance,

  IOpinionBarAndLineChartProps

>((props, ref) => {

  const { formatMessage } = useIntl();

  const {

    chartLoading,

    isError,

    isNodata,

    echartHight = 400,

    chartHeight,

    chartType = 'bar',

    lineOption,

    replaceSeriesOnUpdate = false,

    nodataMsg,

    enableEventAnnotation = false,

    opinionEventConfig,

    controlledEventAnnotation,

  } = props;



  const useOpinionLineChart = shouldUseOpinionLineChart(chartType, lineOption);

  const lineChartRef = useRef<any>(null);

  const xAxisData = useMemo(

    () => getXAxisDataFromLineOption(lineOption),

    [lineOption],

  );



  const shouldFetchEvents =

    enableEventAnnotation && !!opinionEventConfig && !controlledEventAnnotation;



  const internalEventState = useOpinionChartEventsWithDateLimit({

    gameIds: opinionEventConfig?.gameIds || [],

    startTime: opinionEventConfig?.startTime || '',

    endTime: opinionEventConfig?.endTime || '',

    dateType: opinionEventConfig?.dateType,

    entityType: opinionEventConfig?.entityType,

    opinionFilter: opinionEventConfig?.opinionFilter,

    metrics: opinionEventConfig?.metrics,

    tabType: opinionEventConfig?.tabType,

    byType: opinionEventConfig?.byType,

    extByType: opinionEventConfig?.extByType,

    settingStorageKey: opinionEventConfig?.settingStorageKey,

    enabled: shouldFetchEvents,

  });



  const settingStorageKey =

    controlledEventAnnotation?.settingStorageKey ||

    opinionEventConfig?.settingStorageKey ||

    internalEventState.settingStorageKey;



  const eventData =

    controlledEventAnnotation?.eventData ?? internalEventState.eventData;

  const chartEventSetting =

    controlledEventAnnotation?.chartEventSetting ??

    internalEventState.chartEventSetting;

  const changeEventSetting =

    controlledEventAnnotation?.changeEventSetting ??

    internalEventState.changeEventSetting;

  const eventLoading = controlledEventAnnotation

    ? controlledEventAnnotation.eventLoading

    : internalEventState.loading;



  const height = chartHeight || echartHight || 350;

  const shouldBlockChartForEventLoading =
    enableEventAnnotation && eventLoading && !!eventData?.isEmpty;

  const isLoading = Boolean(chartLoading || shouldBlockChartForEventLoading);



  useImperativeHandle(ref, () => ({

    getBarInstance: () => null,

    getPieInstance: () => null,

    getLineInstance: () => lineChartRef.current,

  }));



  if (!useOpinionLineChart) {

    return <ChartsIndex ref={ref} {...props} />;

  }



  return (

    <div className={OPINION_BAR_AND_LINE_CHART_ROOT_CLASS}>

      <Abnormal

        isLoading={isLoading}

        isError={isError}

        isNoData={isNodata || !lineOption}

        component={

          lineOption ? (

            <>

              {enableEventAnnotation && (

                <EventSettingBridge

                  eventData={eventData}

                  xAxisData={xAxisData}

                  dateType={opinionEventConfig?.dateType}

                  chartEventSetting={chartEventSetting}

                  onConfirm={changeEventSetting}

                  settingStorageKey={settingStorageKey}

                />

              )}

              <OpinionStyledLineChart

                ref={lineChartRef}

                lineOption={lineOption}

                height={height}

                chartType={chartType}

                replaceSeriesOnUpdate={replaceSeriesOnUpdate}

                enableEventAnnotation={enableEventAnnotation}

                eventData={eventData}

                chartEventSetting={chartEventSetting}

                dateType={opinionEventConfig?.dateType}

              />

            </>

          ) : null

        }

        style={{ height: echartHight }}

        nodataMsg={nodataMsg || formatMessage({ id: 'common_nodata' })}

      />

    </div>

  );

});



OpinionBarAndLineChart.displayName = 'OpinionBarAndLineChart';



export default OpinionBarAndLineChart as React.ForwardRefExoticComponent<

  IOpinionBarAndLineChartProps & React.RefAttributes<IChartsIndexInstance>

>;



export {

  adaptLineChartData,

  adaptLineOption,

  shouldUseOpinionLineChart,

} from './adaptChartData';

export { OPINION_BAR_AND_LINE_CHART_ROOT_CLASS } from './constants';

export { default as OpinionEventSettingToolbar } from './OpinionEventSettingToolbar';

export {

  useOpinionChartEvents,

  buildOpinionTimelineParams,

  DEFAULT_OPINION_TIMELINE_FILTER,

} from './hooks';

export type {

  IOpinionChartEventData,

  IOpinionChartEventSetting,

  IOpinionEventConfig,

  IUseOpinionChartEventsParams,

  IUseOpinionChartEventsResult,

  TOpinionDateType,

} from './hooks';

export type {

  IAdaptedOpinionLineChartData,

  IOpinionBarAndLineChartData,

  IOpinionBarAndLineFlatDatum,

  IOpinionBarAndLineSeriesData,

  IOpinionBarAndLineSeriesItem,

} from './types';


